import { isAdminPath } from '@/pages/admin/adminPath';
import { localizedPath, splitLangPath, type SeoLang } from '@/seo/site';

/**
 * Every language has its own address so search engines can list each one: Portuguese at the plain addresses
 * (/escunas), the others under a prefix (/es/escunas, /en/escunas, /fr/escunas). These helpers read and build
 * those addresses; router/LangRouter.tsx keeps the address and the site's language in step.
 */

/** The site's own folder: '' on Netlify, a sub-folder on preview hosts (vite.config.ts BASE_PATH). */
export const SITE_BASE = __BASE_PATH__.replace(/\/+$/, '');

export function withoutSiteBase(pathname: string) {
  if (!SITE_BASE) return pathname || '/';
  return pathname.toLowerCase().startsWith(SITE_BASE.toLowerCase()) ? pathname.slice(SITE_BASE.length) || '/' : pathname;
}

/** The language written in an address, or null for a Portuguese (plain) address. */
export const addressLanguage = (pathname: string) => splitLangPath(withoutSiteBase(pathname)).lang;

/**
 * Pages opened from emails, payment returns and the admin keep their address in any language: the ticket
 * link and Mercado Pago's return must keep working exactly as they were sent.
 */
const FIXED_PATHS = ['/ticket', '/reservas', '/pagamento/retorno'];
export const keepsAddress = (path: string) => FIXED_PATHS.includes(path.replace(/\/+$/, '') || '/') || isAdminPath(path);

/** The same page in another language, or null when the address already fits (or must not change). */
export function addressFor(location: { pathname: string; search: string; hash: string }, lang: SeoLang): string | null {
  const { lang: current, path } = splitLangPath(withoutSiteBase(location.pathname));
  if ((current ?? 'pt') === lang || keepsAddress(path)) return null;
  return `${SITE_BASE}${localizedPath(lang, path)}${location.search}${location.hash}`;
}
